import { useState, useEffect } from "react";
import di from "di/di";
import { isRight } from "core/types/Either";
import { experimentClient } from "core/experiment/experimentClient";

export interface UseFeatureFlagResult {
  enabled: boolean;
  isLoading: boolean;
}

/**
 * Returns the enabled state for an Amplitude experiment key.
 *
 * Awaits experimentClient.ensureStarted() before reading the variant. This prevents
 * the init/read race where a child component's effect fires before App.tsx's
 * useAmplitudeExperiment has initialized the client.
 *
 * Known limitation: isLoading: false collapses "client said disabled" and
 * "client was not ready, degraded to false". For wallet (both = hide CTA), this is fine.
 */
export function useFeatureFlag(experimentKey: string): UseFeatureFlagResult {
  const [state, setState] = useState<UseFeatureFlagResult>({
    enabled: false,
    isLoading: true,
  });

  useEffect(() => {
    let cancelled = false;

    experimentClient
      .ensureStarted()
      .then(() => di.getFeatureFlag.execute(experimentKey))
      .then((result) => {
        if (cancelled) return;
        if (isRight(result)) {
          setState({ enabled: result.value.enabled, isLoading: false });
        } else {
          setState({ enabled: false, isLoading: false });
        }
      })
      .catch(() => {
        if (cancelled) return;
        setState({ enabled: false, isLoading: false });
      });

    return () => {
      cancelled = true;
    };
  }, [experimentKey]);

  return state;
}
